import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { Spin } from "antd";
import type { PDFDocumentProxy, RenderTask } from "pdfjs-dist/types/src/pdf";
import {
  ensureLegacyAtSupport,
  getPdfAssetsBaseUrl,
  installPdfJsWorkerGlobal,
  loadPdfJsLegacy,
  loadPdfJsLegacyWorker,
  resolvePdfWorkerSrc,
} from "./pdfjsLegacy";
import {
  clampZoom,
  getCanvasRenderMetrics,
  getFitContainerZoom,
  getMostVisiblePage,
  getPageHorizontalInset,
  normalizePageNumber,
  normalizeViewerMode,
  ZOOM_STEP,
} from "./pdfViewerMath";

export type PDFViewerMode = "pagination" | "scroll" | "continuous";

type PdfJsLegacyModule = Awaited<ReturnType<typeof loadPdfJsLegacy>>;
type PdfDocumentLoadingTask = ReturnType<PdfJsLegacyModule["getDocument"]>;

export interface PDFViewerRef {
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  goToPage: (page: number) => void;
  getCurrentPage: () => number;
  getTotalPages: () => number;
  getZoomLevel: () => number;
}

export interface PDFViewerProps {
  file: string;
  mode?: PDFViewerMode;
  defaultZoom?: number;
  className?: string;
  style?: CSSProperties;
  viewerClassName?: string;
  viewerStyle?: CSSProperties;
  pageClassName?: string;
  pageStyle?: CSSProperties;
  canvasClassName?: string;
  canvasStyle?: CSSProperties;
  onLoadSuccess?: (pdf: PDFDocumentProxy) => void;
  onLoadError?: (error: unknown) => void;
  onPageChange?: (page: number) => void;
  onZoomChange?: (zoom: number) => void;
}

const rootStyle = {
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
  minHeight: 0,
} as const;

const viewerBaseStyle = {
  position: "relative",
  display: "block",
  width: "100%",
  height: "100%",
  flex: 1,
  minHeight: 0,
  overflow: "auto",
} as const;

const pagesContainerStyle = {
  display: "flex",
  flexDirection: "column",
} as const;

const centeredStateStyle = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  minHeight: 300,
  padding: 40,
  textAlign: "center",
} as const;

function getContainerContentWidth(container: HTMLDivElement) {
  const computedStyle = window.getComputedStyle(container);
  const paddingLeft = Number.parseFloat(computedStyle.paddingLeft || "0") || 0;
  const paddingRight = Number.parseFloat(computedStyle.paddingRight || "0") || 0;
  return Math.max(container.clientWidth - paddingLeft - paddingRight, 0);
}

function getPageHorizontalInsetFromElement(pageWrapper: HTMLDivElement | undefined) {
  if (!pageWrapper) {
    return 0;
  }

  const computedStyle = window.getComputedStyle(pageWrapper);
  return getPageHorizontalInset({
    paddingLeft: computedStyle.paddingLeft,
    paddingRight: computedStyle.paddingRight,
    borderLeftWidth: computedStyle.borderLeftWidth,
    borderRightWidth: computedStyle.borderRightWidth,
  });
}

function isIgnorableRenderError(error: unknown) {
  if (!(error instanceof Error)) {
    return false;
  }

  return (
    error.name === "RenderingCancelledException" ||
    error.message.includes("Invalid page request")
  );
}

export const PDFViewer = forwardRef<PDFViewerRef, PDFViewerProps>(
  (
    {
      file,
      mode = "pagination",
      defaultZoom = 1,
      className,
      style,
      viewerClassName,
      viewerStyle,
      pageClassName,
      pageStyle,
      canvasClassName,
      canvasStyle,
      onLoadSuccess,
      onLoadError,
      onPageChange,
      onZoomChange,
    },
    ref,
  ) => {
    const normalizedMode = normalizeViewerMode(mode);
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());
    const pageWrapperRefs = useRef<Map<number, HTMLDivElement>>(new Map());
    const renderTasksRef = useRef<Map<number, RenderTask>>(new Map());
    const pdfDocRef = useRef<PDFDocumentProxy | null>(null);
    const pdfLibRef = useRef<PdfJsLegacyModule | null>(null);
    const pendingScrollPageRef = useRef<number | null>(null);
    const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const renderSequenceRef = useRef(0);
    const currentPageRef = useRef(1);
    const totalPagesRef = useRef(0);
    const zoomRef = useRef(defaultZoom);
    const autoZoomRef = useRef(defaultZoom);
    const isManualZoomRef = useRef(false);
    const onLoadSuccessRef = useRef(onLoadSuccess);
    const onLoadErrorRef = useRef(onLoadError);
    const onPageChangeRef = useRef(onPageChange);
    const onZoomChangeRef = useRef(onZoomChange);
    const loadSuccessReportedRef = useRef(false);
    const loadErrorReportedRef = useRef(false);
    const [pdfJsReady, setPdfJsReady] = useState(false);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [zoom, setZoom] = useState(defaultZoom);
    const [isManualZoom, setIsManualZoom] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<unknown>(null);

    const proxyUrl = useMemo(() => {
      if (!file) return "";

      const useProxy = import.meta.env.DEV;
      const domain =
        import.meta.env.VITE_OSS_DOMAIN ||
        "https://bee-ai-test.oss-cn-shenzhen.aliyuncs.com";
      const proxyPath = import.meta.env.VITE_OSS_PROXY_PATH || "/oss-proxy";

      if (!useProxy) return file;
      if (!file.includes(domain)) return file;

      return file.replace(domain, proxyPath);
    }, [file]);

    const pageNumbers = useMemo(
      () => Array.from({ length: totalPages }, (_, index) => index + 1),
      [totalPages],
    );

    const notifyLoadSuccessOnce = useCallback((doc: PDFDocumentProxy) => {
      if (loadSuccessReportedRef.current || loadErrorReportedRef.current) {
        return;
      }

      loadSuccessReportedRef.current = true;
      onLoadSuccessRef.current?.(doc);
    }, []);

    const notifyLoadErrorOnce = useCallback((error: unknown) => {
      if (loadSuccessReportedRef.current || loadErrorReportedRef.current) {
        return;
      }

      loadErrorReportedRef.current = true;
      onLoadErrorRef.current?.(error);
    }, []);

    const cancelRenderTasks = useCallback(async () => {
      const tasks = Array.from(renderTasksRef.current.values());
      renderTasksRef.current.clear();

      tasks.forEach((task) => {
        try {
          task.cancel();
        } catch {
          // Ignore cleanup errors from superseded renders.
        }
      });

      await Promise.allSettled(tasks.map((task) => task.promise));
    }, []);

    const destroyPdfDocument = useCallback(async () => {
      const pdfDoc = pdfDocRef.current;
      pdfDocRef.current = null;

      if (!pdfDoc) {
        return;
      }

      try {
        await pdfDoc.destroy();
      } catch {
        // Ignore cleanup errors.
      }
    }, []);

    const updateCurrentPage = useCallback((nextPage: number) => {
      setCurrentPage((prev) => (prev === nextPage ? prev : nextPage));
    }, []);

    const scrollToPage = useCallback((pageNumber: number) => {
      const container = containerRef.current;
      const pageWrapper = pageWrapperRefs.current.get(pageNumber);

      if (!container || !pageWrapper || pageWrapper.clientHeight === 0) {
        return false;
      }

      container.scrollTo({
        top: pageWrapper.offsetTop,
        behavior: "auto",
      });
      pendingScrollPageRef.current = null;
      return true;
    }, []);

    const setNormalizedPage = useCallback(
      (requestedPage: number) => {
        const nextPage = normalizePageNumber(requestedPage, totalPagesRef.current);
        updateCurrentPage(nextPage);

        if (normalizedMode === "scroll") {
          pendingScrollPageRef.current = nextPage;
          requestAnimationFrame(() => {
            scrollToPage(nextPage);
          });
        }
      },
      [normalizedMode, scrollToPage, updateCurrentPage],
    );

    const computeAutoZoom = useCallback(
      async (pdfDoc?: PDFDocumentProxy) => {
        if (isManualZoomRef.current) {
          return;
        }

        const doc = pdfDoc || pdfDocRef.current;
        const container = containerRef.current;

        if (!doc || !container) {
          return;
        }

        try {
          const page = await doc.getPage(1);
          const viewport = page.getViewport({ scale: 1 });
          const pageHorizontalInset = getPageHorizontalInsetFromElement(
            pageWrapperRefs.current.get(1),
          );
          const nextZoom = getFitContainerZoom({
            containerWidth: getContainerContentWidth(container),
            pageWidth: viewport.width,
            defaultZoom,
            pageHorizontalInset,
          });

          autoZoomRef.current = nextZoom;
          setZoom((prev) => (prev === nextZoom ? prev : nextZoom));
        } catch (error) {
          console.error("Auto-scale calculation failed:", error);
        }
      },
      [defaultZoom],
    );

    const syncCurrentPageFromScroll = useCallback(() => {
      if (normalizedMode !== "scroll" || !containerRef.current) {
        return;
      }

      const container = containerRef.current;
      const pages = Array.from(pageWrapperRefs.current.entries()).map(
        ([pageNumber, element]) => ({
          pageNumber,
          offsetTop: element.offsetTop,
          height: element.clientHeight,
        }),
      );

      if (!pages.length) {
        return;
      }

      const nextPage = getMostVisiblePage({
        scrollTop: container.scrollTop,
        containerHeight: container.clientHeight,
        fallbackPage: currentPageRef.current,
        pages,
      });

      updateCurrentPage(nextPage);
    }, [normalizedMode, updateCurrentPage]);

    const renderPage = useCallback(
      async (pageNumber: number, sequence: number) => {
        const doc = pdfDocRef.current;
        if (!doc || sequence !== renderSequenceRef.current) {
          return;
        }

        try {
          const previousTask = renderTasksRef.current.get(pageNumber);
          if (previousTask) {
            previousTask.cancel();
            try {
              await previousTask.promise;
            } catch {
              // Ignore cancellation errors from superseded renders.
            }
            renderTasksRef.current.delete(pageNumber);
          }

          const page = await doc.getPage(pageNumber);
          if (sequence !== renderSequenceRef.current) {
            return;
          }

          const canvas = canvasRefs.current.get(pageNumber);
          if (!canvas) {
            return;
          }

          const context = canvas.getContext("2d");
          if (!context) {
            throw new Error(
              `Failed to acquire 2D canvas context for page ${pageNumber}`,
            );
          }

          const viewport = page.getViewport({ scale: zoom });
          const outputScale = window.devicePixelRatio || 1;
          const metrics = getCanvasRenderMetrics({
            viewportWidth: viewport.width,
            viewportHeight: viewport.height,
            outputScale,
          });

          canvas.width = metrics.canvasWidth;
          canvas.height = metrics.canvasHeight;
          canvas.style.width = metrics.cssWidth;
          canvas.style.height = metrics.cssHeight;
          context.setTransform(1, 0, 0, 1, 0, 0);

          const renderTask = page.render({
            canvasContext: context,
            viewport,
            transform: metrics.transform ?? undefined,
          });
          renderTasksRef.current.set(pageNumber, renderTask);

          try {
            await renderTask.promise;
          } finally {
            renderTasksRef.current.delete(pageNumber);
            if (pendingScrollPageRef.current === pageNumber) {
              requestAnimationFrame(() => {
                scrollToPage(pageNumber);
              });
            }
          }
        } catch (error) {
          if (isIgnorableRenderError(error)) {
            return;
          }

          console.error(`Error rendering page ${pageNumber}:`, error);
          if (pageNumber === 1) {
            setLoadError(error);
            notifyLoadErrorOnce(error);
          }
        }
      },
      [notifyLoadErrorOnce, scrollToPage, zoom],
    );

    useImperativeHandle(
      ref,
      () => ({
        zoomIn: () => {
          setIsManualZoom(true);
          setZoom((prev) => clampZoom(prev + ZOOM_STEP));
        },
        zoomOut: () => {
          setIsManualZoom(true);
          setZoom((prev) => clampZoom(prev - ZOOM_STEP));
        },
        resetZoom: () => {
          setIsManualZoom(false);
          setZoom(autoZoomRef.current || defaultZoom);
        },
        goToPage: (pageNumber) => {
          setNormalizedPage(pageNumber);
        },
        getCurrentPage: () => currentPageRef.current,
        getTotalPages: () => totalPagesRef.current,
        getZoomLevel: () => zoomRef.current,
      }),
      [defaultZoom, setNormalizedPage],
    );

    useEffect(() => {
      onLoadSuccessRef.current = onLoadSuccess;
    }, [onLoadSuccess]);

    useEffect(() => {
      onLoadErrorRef.current = onLoadError;
    }, [onLoadError]);

    useEffect(() => {
      onPageChangeRef.current = onPageChange;
    }, [onPageChange]);

    useEffect(() => {
      onZoomChangeRef.current = onZoomChange;
    }, [onZoomChange]);

    useEffect(() => {
      currentPageRef.current = currentPage;
      onPageChangeRef.current?.(currentPage);
    }, [currentPage]);

    useEffect(() => {
      totalPagesRef.current = totalPages;
    }, [totalPages]);

    useEffect(() => {
      zoomRef.current = zoom;
      onZoomChangeRef.current?.(zoom);
    }, [zoom]);

    useEffect(() => {
      isManualZoomRef.current = isManualZoom;
    }, [isManualZoom]);

    useEffect(() => {
      const initPdfJs = async () => {
        if (pdfLibRef.current) {
          return;
        }

        try {
          ensureLegacyAtSupport();
          const [pdfjsLib, pdfjsWorker] = await Promise.all([
            loadPdfJsLegacy(),
            loadPdfJsLegacyWorker(),
          ]);

          installPdfJsWorkerGlobal(pdfjsWorker);
          pdfjsLib.GlobalWorkerOptions.workerSrc = resolvePdfWorkerSrc();
          pdfLibRef.current = pdfjsLib;
          setPdfJsReady(true);
        } catch (error) {
          console.error("Failed to load PDF.js library:", error);
          setLoadError(error);
          setLoading(false);
          notifyLoadErrorOnce(error);
        }
      };

      void initPdfJs();
    }, [notifyLoadErrorOnce]);

    useEffect(() => {
      setCurrentPage(1);
      setTotalPages(0);
      setZoom(defaultZoom);
      setIsManualZoom(false);
      autoZoomRef.current = defaultZoom;
    }, [defaultZoom, proxyUrl]);

    useEffect(() => {
      let isCancelled = false;
      let loadingTask: PdfDocumentLoadingTask | null = null;

      const loadPdf = async () => {
        if (!pdfJsReady || !pdfLibRef.current) {
          return;
        }

        if (!proxyUrl) {
          const error = new Error("PDF URL not provided");
          setLoadError(error);
          setLoading(false);
          notifyLoadErrorOnce(error);
          return;
        }

        setLoading(true);
        setLoadError(null);
        setTotalPages(0);
        setCurrentPage(1);
        setIsManualZoom(false);
        setZoom(defaultZoom);
        autoZoomRef.current = defaultZoom;
        pendingScrollPageRef.current = null;
        loadSuccessReportedRef.current = false;
        loadErrorReportedRef.current = false;
        renderSequenceRef.current += 1;
        canvasRefs.current.clear();
        pageWrapperRefs.current.clear();

        await cancelRenderTasks();
        await destroyPdfDocument();

        try {
          const assetsBaseUrl = getPdfAssetsBaseUrl();
          const cMapUrl = `${assetsBaseUrl}cmaps/`;
          const standardFontDataUrl = `${assetsBaseUrl}standard_fonts/`;

          loadingTask = pdfLibRef.current.getDocument({
            url: proxyUrl,
            cMapUrl,
            cMapPacked: true,
            standardFontDataUrl,
            disableRange: true,
            disableAutoFetch: true,
          });

          const doc = await loadingTask.promise;

          if (isCancelled) {
            await doc.destroy();
            return;
          }

          pdfDocRef.current = doc;
          setTotalPages(doc.numPages);
          setCurrentPage(1);
          setLoading(false);
          notifyLoadSuccessOnce(doc);

          requestAnimationFrame(() => {
            void computeAutoZoom(doc);
          });
        } catch (error) {
          if (isCancelled) {
            return;
          }

          console.error("Error loading PDF file:", error);
          setLoadError(error);
          setLoading(false);
          notifyLoadErrorOnce(error);
        }
      };

      void loadPdf();

      return () => {
        isCancelled = true;
        if (loadingTask) {
          Promise.resolve(loadingTask.destroy()).catch(() => {
            // Ignore loading task cleanup errors.
          });
        }
      };
    }, [
      cancelRenderTasks,
      computeAutoZoom,
      defaultZoom,
      destroyPdfDocument,
      notifyLoadErrorOnce,
      notifyLoadSuccessOnce,
      pdfJsReady,
      proxyUrl,
    ]);

    useEffect(() => {
      if (normalizedMode !== "pagination" || !totalPages) {
        return;
      }

      const renderCurrentPage = async () => {
        const sequence = (renderSequenceRef.current += 1);
        await cancelRenderTasks();
        await renderPage(currentPage, sequence);
      };

      void renderCurrentPage();
    }, [cancelRenderTasks, currentPage, normalizedMode, renderPage, totalPages]);

    useEffect(() => {
      if (normalizedMode !== "scroll" || !totalPages) {
        return;
      }

      const renderAllPages = async () => {
        const sequence = (renderSequenceRef.current += 1);
        await cancelRenderTasks();

        for (const pageNumber of pageNumbers) {
          if (sequence !== renderSequenceRef.current) {
            return;
          }
          await renderPage(pageNumber, sequence);
        }

        syncCurrentPageFromScroll();
      };

      void renderAllPages();
    }, [
      cancelRenderTasks,
      normalizedMode,
      pageNumbers,
      renderPage,
      syncCurrentPageFromScroll,
      totalPages,
    ]);

    useEffect(() => {
      if (normalizedMode !== "scroll" || pendingScrollPageRef.current == null) {
        return;
      }

      const pageNumber = pendingScrollPageRef.current;
      requestAnimationFrame(() => {
        if (pageNumber != null) {
          scrollToPage(pageNumber);
        }
      });
    }, [normalizedMode, scrollToPage, totalPages, zoom]);

    useEffect(() => {
      const container = containerRef.current;
      if (!container || isManualZoom) {
        return;
      }

      const scheduleAutoZoom = () => {
        if (resizeTimerRef.current) {
          clearTimeout(resizeTimerRef.current);
        }

        resizeTimerRef.current = setTimeout(() => {
          void computeAutoZoom();
        }, 120);
      };

      scheduleAutoZoom();

      if ("ResizeObserver" in window) {
        const resizeObserver = new ResizeObserver(() => {
          scheduleAutoZoom();
        });

        resizeObserver.observe(container);

        return () => {
          resizeObserver.disconnect();
          if (resizeTimerRef.current) {
            clearTimeout(resizeTimerRef.current);
            resizeTimerRef.current = null;
          }
        };
      }

      window.addEventListener("resize", scheduleAutoZoom);

      return () => {
        window.removeEventListener("resize", scheduleAutoZoom);
        if (resizeTimerRef.current) {
          clearTimeout(resizeTimerRef.current);
          resizeTimerRef.current = null;
        }
      };
    }, [computeAutoZoom, isManualZoom]);

    useEffect(() => {
      return () => {
        if (resizeTimerRef.current) {
          clearTimeout(resizeTimerRef.current);
        }
        void cancelRenderTasks();
        void destroyPdfDocument();
      };
    }, [cancelRenderTasks, destroyPdfDocument]);

    const renderPageNode = (pageNumber: number) => (
      <div
        key={pageNumber}
        id={`pdf-page-${pageNumber}`}
        ref={(element) => {
          if (element) {
            pageWrapperRefs.current.set(pageNumber, element);
          } else {
            pageWrapperRefs.current.delete(pageNumber);
          }
        }}
        className={pageClassName}
        style={pageStyle}
      >
        <canvas
          ref={(element) => {
            if (element) {
              canvasRefs.current.set(pageNumber, element);
            } else {
              canvasRefs.current.delete(pageNumber);
            }
          }}
          className={canvasClassName}
          style={canvasStyle}
        />
      </div>
    );

    const viewerBody = (() => {
      if (!proxyUrl) {
        return <div style={centeredStateStyle}>PDF URL未提供</div>;
      }

      if (loading) {
        return (
          <div style={centeredStateStyle}>
            <Spin tip="正在載入檔案..." />
          </div>
        );
      }

      if (loadError) {
        const message =
          loadError instanceof Error ? loadError.message : String(loadError);
        return (
          <div style={centeredStateStyle}>
            <div style={{ color: "red", marginBottom: 12 }}>無法預覽檔案</div>
            <div style={{ fontSize: 12, color: "#666" }}>{message}</div>
          </div>
        );
      }

      if (!totalPages) {
        return null;
      }

      if (normalizedMode === "pagination") {
        return renderPageNode(currentPage);
      }

      return <div style={pagesContainerStyle}>{pageNumbers.map(renderPageNode)}</div>;
    })();

    return (
      <div className={className} style={{ ...rootStyle, ...style }}>
        <div
          ref={containerRef}
          className={viewerClassName}
          style={{ ...viewerBaseStyle, ...viewerStyle }}
          onScroll={normalizedMode === "scroll" ? syncCurrentPageFromScroll : undefined}
        >
          {viewerBody}
        </div>
      </div>
    );
  },
);

PDFViewer.displayName = "PDFViewer";
